import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { handleValidateCodevalue, whatsappProviders } from '../Utility/constant';
import { StyledConfigurationContainer, StyledLeadContainer, StyledSubHeader } from './Styled';
import BiztantuLoader from './Loader';
import { Box, Tooltip, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import CheckIcon from '@mui/icons-material/Check';
import CancelIcon from '@mui/icons-material/Close';
import tableBodyicon from '../Assets/images/tablebody_edit.png';
import tableEditicon from '../Assets/images/tableheader_edit.png';
import disabledEdit from '../Assets/images/disabled_edit.png';
import DataGridTable from './DataGrid';
import { useSnackbar } from 'notistack';
import { GridActionsCellItem, GridRowModes } from '@mui/x-data-grid';
import { requestHideLoader, requestShowLoader } from '../Store/Global/action';
import { createRequestData, getRequestData, updateRequestData } from '../Utility/apiHelper';
import SelectBoxWithLabel from './SelectBoxWithLable';
import { PasswordEditInputCell, PasswordViewCell } from './EmailConfig';
import '../Pages/Template/template.css';

const WhatsappSettings = () => {
  const isLoading = useSelector(state => state?.global?.isLoading);
  const internationalise = useSelector(state => state?.internationalise?.currentHashMapData);
  const specificRolePermission = useSelector(state => state?.owner?.rolebasedData);

  const [dataGridData, setDatagridData] = useState([]);
  const [rowModesModel, setRowModesModel] = useState({});

  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();

  const canEdit = specificRolePermission?.isNotificationManage;

  useEffect(() => {
    getWhatsappDetails();
  }, []);

  const addEmptyRow = () => {
    setDatagridData([{
      id: Date.now(),
      userName: "",
      password: "",
      whatsAppProvider: "",
      isNew: true,
    }]);
  };

const getWhatsappDetails = async () => {
  try {
    dispatch(requestShowLoader());

    const url = `${process.env.REACT_APP_BASE_URL}whatsapp`;
    const response = await getRequestData(url);

    let data = response?.data;

    if (data && !Array.isArray(data)) {
      data = [data];
    }

    if (response?.status === 200 && data?.length > 0) {
      setDatagridData(
        data.map((item, index) => ({
          id: item.id || index + 1,
          userName: item.userName || "",
          password: item.password || "",
          status: item.status || "",
          whatsAppProvider: item.whatsAppProvider || "",
          isNew: false,
        }))
      );
    } else {
      addEmptyRow();
    }
  } catch (error) {
    console.error(error);
    addEmptyRow();
  } finally {
    dispatch(requestHideLoader());
  }
};

  const handleEditClick = useCallback((id) => () => {
    setRowModesModel(prev => ({ ...prev, [id]: { mode: GridRowModes.Edit } }));
  }, []);

  const handleSaveClick = useCallback((id) => () => {
    setRowModesModel(prev => ({ ...prev, [id]: { mode: GridRowModes.View } }));
  }, []);

  const handleCancelClick = useCallback((id) => () => {
    setRowModesModel(prev => ({
      ...prev,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    }));
  }, []);

  const processRowUpdate = useCallback(async (newRow, oldRow) => {
    if (!newRow?.userName || !newRow?.password || !newRow?.whatsAppProvider) {
      enqueueSnackbar(handleValidateCodevalue(internationalise, "COMMON_MSG_MANDATORY_FIELDS"),{ variant: "error" });
      handleEditClick(newRow?.id)()
      return newRow;
    }
    try {
      dispatch(requestShowLoader());

      const payload = {
        userName: newRow.userName.trim(),
        password: newRow.password.trim(),
        whatsAppProvider: newRow.whatsAppProvider,
      };

      let response;
      const baseUrl = `${process.env.REACT_APP_BASE_URL}whatsapp`;

      if (newRow.isNew) {
        response = await createRequestData(payload, baseUrl);
      } else {
        response = await updateRequestData(payload, `${baseUrl}/${newRow.id}`);
      }

      if (response?.status === 200 || response?.status === 201) {
        enqueueSnackbar(handleValidateCodevalue(internationalise, (response?.data?.message || "WHATSAPP_CRED_SAVED_SUCCESSFULLY")), { variant: "success" });
                            
        const updatedRow = {
          ...newRow,
          id: response?.data?.id || newRow.id,
          isNew: false,
        };

        setDatagridData(prev =>
          prev.map(row => (row.id === newRow.id ? updatedRow : row))
        );

        return updatedRow;
      }
      else{
        enqueueSnackbar(handleValidateCodevalue(internationalise, (response?.propererror.data?.message || "COMMON_PLEASE_TRY_AGAIN")), { variant: "error" });
      }
    } catch (error) {
      console.error(error);
      return oldRow; 
    } finally {
      dispatch(requestHideLoader());
    }
  }, [enqueueSnackbar, internationalise]);

  const columns = useMemo(() => [
    {
      field: 'actions',
      type: 'actions',
      headerName: (
        <Tooltip title={handleValidateCodevalue(internationalise, 'COMMON_ACTION')}>
          <img src={tableEditicon} className="provider-settings-edit-icon" />
        </Tooltip>
      ),
      width: 100,
      getActions: ({ id }) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

        if (isInEditMode) {
          return [
            <GridActionsCellItem
              icon={<CheckIcon />}
              label="Save"
              onClick={handleSaveClick(id)}
            />,
            <GridActionsCellItem
              icon={<CancelIcon />}
              label="Cancel"
              onClick={handleCancelClick(id)}
            />,
          ];
        }

        return [
          <GridActionsCellItem
            icon={<img src={!canEdit ? disabledEdit : tableBodyicon} />}
            label="Edit"
            onClick={handleEditClick(id)}
            disabled={!canEdit}
          />,
        ];
      },
    },
    { 
        field: "userName",
        headerName: handleValidateCodevalue(internationalise, 'WAPP_COLUMN_USERNAME'),
        flex: 1, editable: true 
    },
    { 
        field: "password",
        headerName: handleValidateCodevalue(internationalise, 'WAPP_COLUMN_PASSWORD'),
        flex: 1, 
        editable: true ,
        renderCell: (params) => <PasswordViewCell {...params} canEdit={canEdit} />, 
        renderEditCell: (params) => <PasswordEditInputCell {...params} canEdit={canEdit} />,
    },
    {
      field: "whatsAppProvider",
      headerName:handleValidateCodevalue(internationalise, 'WAPP_COLUMN_PROVIDER'),
      flex: 1,
      editable: true,
      renderEditCell: (params) => (
        <SelectBoxWithLabel
          select
          required
          name="whatsAppProvider"
          value={params.value || ""}
          optionMenuItem={whatsappProviders}
          handleOnChange={(event) => {
            params.api.setEditCellValue({
              id: params.id,
              field: "whatsAppProvider",
              value: event.target.value,
            });
          }}
        />
      ),
    },
  ], [rowModesModel, canEdit, internationalise]);

  return (
    <BiztantuLoader isChildren open={isLoading}>
      <StyledLeadContainer>
        <StyledConfigurationContainer className="provider-settings-header">
          <Typography variant='h6'>
            {handleValidateCodevalue(internationalise, 'WHATSAPP_SETTINGS_TITLE')}
          </Typography>
        </StyledConfigurationContainer>
      </StyledLeadContainer>

      <Box className="hide-scrollbar provider-settings-content">
        <StyledSubHeader className="provider-settings-subheader">
          {handleValidateCodevalue(internationalise, 'SET_WHATSAPP_PROVIDER_DETAILS')}
        </StyledSubHeader>

        <DataGridTable
          height='200px'
          rowModesModel={rowModesModel}
          columns={columns}
          isRequiredToolBar={false}
          isCheckboxReq = {false}
          dataGridData={dataGridData}
          handleProcessRowUpdate={processRowUpdate}
          hideFooter
        />
      </Box>
    </BiztantuLoader>
  );
};

export default WhatsappSettings;